import React, {useEffect, useState} from 'react';
import propTypes from 'prop-types';
import {connect} from 'react-redux';
import TablePagination from '../../../../../components-new/table/TablePagination';
import config from '../../../../../config';
import HistoryTable from './HistoryTable';
import SelectedConversationHistory from './ConversationHistory';
import getHistoryPage from './api';
import {
    clearSelectedConversation as clearSelectedConversationFromStore,
    loadOlderMessagesRequested,
    selectConversationRequested,
} from '../../../../../services/chat/action';
import './index.css';
import ChatMoreInfoModal from '../../../../../components-new/general/ChatMoreInfoModal';
import TableFilters from "./TableFilters";
import {handleSort} from "../../../../../utils/TableUtils";
import OperatorProfileDrawer from "../../../../../components-new/general/OperatorProfileDrawer";
import {hasManagementPermission} from "../../../../../utils/General";

const styles = {
    /*
     * The page's own inset is the only one now - see --ds-page-inset. This used
     * to be a white card with a 30px margin and 40/60 padding inside it, which
     * on a white page is an invisible box that pushes the content a hundred
     * pixels off the panel. What is left here is what the element actually does:
     * it scrolls.
     */
    /*
     * The height is what makes the table scroll rather than the page.
     *
     * It was commented out, and without a height this column simply grew: at 50
     * rows per page the filters and the pagination scrolled away with the table,
     * and the scrollbar belonged to the whole content area. .history-table-scroll
     * asks for the leftover space (flex: 1 1 auto, min-height: 0), and leftover
     * of an unbounded parent is not a number.
     *
     * 100% rather than the calc that used to be here: the content area around it
     * already has a resolved height, so this does not need to know how tall the
     * header, the gutters and the insets happen to be - which is what made that
     * calc wrong the moment any of them changed.
     */
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: '100%',
        minHeight: 0,
        // only the table scrolls; the filters stay at the top and the pagination
        // at the bottom
        overflow: 'hidden',
    },
    header: {
        color: '#3D568F',
        fontSize: '22px',
        marginBottom: '50px',
    },
};

const History = ({
                     user,
                     company,
                     conversation,
                     clearSelectedConversationFromStore,
                     selectConversation,
                     loadOlderMessages,
                     addNotification
                 }) => {

    const [conversationsList, setConversationsList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false)

    const [isFetching, setIsFetching] = useState(false);
    const [filters, setFilters] = useState({})

    const [pageable, setPageable] = useState({
        page: 0,
        size: 10,
        sort: 'lastModifiedAt,desc'
    })

    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [propConv, setPropConv] = useState();
    const [operatorProfile, setOperatorProfile] = useState(null);

    useEffect(() => {
        if (Object.entries(filters).length > 0) {
            getHistoryPageData();
        }
    }, [filters, pageable]);

    const getHistoryPageData = () => {
        if (isFetching) return
        setIsFetching(true);

        getHistoryPage(filters, pageable).then((result) => {
            if (result && result.content && result.pageable) {
                setConversationsList(result.content)
                setTotalElements(result.totalElements)
                setTotalPages(result.totalPages === 0 ? 1 : result.totalPages)
            } else {
                setConversationsList([])
                setTotalElements(0)
                setTotalPages(1)
            }
        }).catch(e => {
            setConversationsList([])
            setTotalElements(0)
            setTotalPages(1)
        }).finally(() => {
            setIsFetching(false);
        })
    };

    const closeConversationHistory = () => {
        clearSelectedConversationFromStore();
    };

    const showConversationHistory = (conversationInfo) => {
        selectConversation({
            id: conversationInfo.conversationId,
            companyId: conversationInfo.companyId,
            platform: conversationInfo.platform,
            userFirstName: conversationInfo.customerName,
            userThumbUrl: conversationInfo.visitorThumbUrl
        });
    };

    const openTrModal = (conversation) => {
        setPropConv(conversation);
        setIsModalOpen(true);
    };

    const openOperatorProfile = (conversation) => {
        if (!user || !user.authorities || !hasManagementPermission(user)) return;
        const nameParts = (conversation.operatorFullName || '').trim().split(/\s+/).filter(Boolean);
        setOperatorProfile({
            id: conversation.operatorId,
            fullName: conversation.operatorFullName,
            firstName: nameParts[0],
            lastName: nameParts.slice(1).join(' '),
            companyId: conversation.companyId,
        });
    };

    return (
        <div className={'fade-in'} style={styles.wrapper}>
            <TableFilters
                company={company}
                user={user}
                onFilterChange={(filters) => {
                    setFilters(filters);
                    setPageable(prev => {
                        return {
                            ...prev,
                            page: 0
                        }
                    })
                }}
                isFetching={isFetching}
            />

            <HistoryTable
                user={user}
                addNotification={addNotification}
                openTrModal={openTrModal}
                openOperatorProfile={openOperatorProfile}
                conversationsList={conversationsList}
                showConversationHistory={showConversationHistory}
                onSort={(col) => handleSort(col, pageable, setPageable)}
                pageable={pageable}
                isFetching={isFetching}
            />

            <TablePagination
                totalRows={totalElements}
                numberOfPages={totalPages}
                handleChangeRowsPerPage={(size) => isFetching ? '' : setPageable({...pageable, size})}
                rowsPerPage={pageable.size}
                curPage={pageable.page}
                selectPage={(page) => isFetching ? '' : setPageable({...pageable, page})}
            />
            {conversation.id !== null &&
                <SelectedConversationHistory
                    isOpen={conversation.id !== null}
                    tariff={company.tariff}
                    conversation={conversation}
                    user={user}
                    company={company}
                    loadOlderMessages={loadOlderMessages}
                    closeWindow={closeConversationHistory}
                />
            }
            {isModalOpen &&
                <ChatMoreInfoModal
                    isOpen={isModalOpen}
                    onRequestClose={() => setIsModalOpen(false)}
                    useProvidedDetails
                    company={company}
                    showConversationHistory={showConversationHistory}
                    conversation={propConv}
                />
            }
            {user && user.authorities && hasManagementPermission(user) && (
                <OperatorProfileDrawer
                    open={!!operatorProfile}
                    onClose={() => setOperatorProfile(null)}
                    operator={operatorProfile}
                    parentCompanyId={company.id}
                    filters={{
                        ...filters,
                        companies: filters.companies || (company.subs ? company.subs.map(sub => sub.id) : []),
                    }}
                />
            )}

        </div>
    );
};

History.propTypes = {
    user: propTypes.object.isRequired,
    company: propTypes.object.isRequired,
    conversation: propTypes.object.isRequired,
    clearSelectedConversationFromStore: propTypes.func.isRequired,
    selectConversation: propTypes.func.isRequired,
    loadOlderMessages: propTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    conversation: state.chat.conversation,
});

const mapDispatchToProps = (dispatch) => ({
    selectConversation: (conversation) => {
        dispatch(selectConversationRequested(conversation));
    },
    loadOlderMessages: () => {
        dispatch(loadOlderMessagesRequested());
    },
    clearSelectedConversationFromStore: () => {
        dispatch(clearSelectedConversationFromStore());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(History);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/history/index.jsx